import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ChatBox from "../components/ChatBox";
import StreamViewingMenu from "../components/StreamViewingMenu";
import socket from "../lib/socket";
import { ImaController } from "../lib/imaController";
import "./Stream.css";
import CheerOverlayLayer from "../components/CheerOverlayLayer";
import GasPointsOverlayLayer from "../components/GasPointsOverlayLayer";
import RpsOverlayLayer from "../components/RpsOverlayLayer";

const HILLTOP_PREROLL_TAG =
  "https://pointless-plenty.com/d-m_F/zKd.GLNuvLZdGDUF/ae/mV9euAZ/UwlukFPSTcY/4/OFDxEj5uOyD/EDtANjj/g_4GMMTZkk4ZN/SMZssGabW/1CpedIDE0CxL";

const DEFAULT_PREROLL_TAG =
  process.env.REACT_APP_IMA_AD_TAG_URL?.trim() || HILLTOP_PREROLL_TAG;

const LIVE_MIDROLL_INTERVAL_MS = 30 * 60 * 1000;
const ACTIVE_WATCH_TICK_MS = 5000;

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getCurrentUserEmail() {
  const rawUser =
    localStorage.getItem("ga_user") ||
    localStorage.getItem("user") ||
    localStorage.getItem("member_user") ||
    "";
  const parsed = rawUser ? safeJsonParse(rawUser) : null;
  const email =
    parsed?.email ||
    localStorage.getItem("member_email") ||
    localStorage.getItem("email") ||
    "";
  return String(email || "").trim().toLowerCase();
}

function getApiBase() {
  const raw = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || "").trim();
  const cleaned = raw.replace(/\/$/, "").replace(/\/api$/i, "");
  return cleaned || "https://streamsite-v8dd.onrender.com";
}

function withCorrelator(base) {
  if (!base) return "";
  if (base.includes("correlator=")) return base;
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}correlator=${Date.now()}`;
}

function canAutoplayWithSound() {
  try {
    const policy = navigator.getAutoplayPolicy?.("mediaelement");
    if (policy) return policy === "allowed";
  } catch {
    // Fall back to sticky user activation.
  }

  try {
    return !!navigator.userActivation?.hasBeenActive;
  } catch {
    return false;
  }
}

const MUX_VIEWING_OPTIONS = [
  {
    value: "auto",
    label: "Auto",
    shortLabel: "Auto",
    detail: "Adapts quality to the connection automatically.",
  },
  {
    value: "270p",
    label: "Data Saver • 270p",
    shortLabel: "Data Saver",
    detail: "Strongest bandwidth-saving mode for slow or metered data.",
  },
  {
    value: "360p",
    label: "Low • 360p",
    shortLabel: "360p",
    detail: "Lower data use with a clearer picture than Data Saver.",
  },
  {
    value: "480p",
    label: "Balanced • 480p",
    shortLabel: "480p",
    detail: "A good middle ground for mobile data.",
  },
  {
    value: "720p",
    label: "HD • 720p",
    shortLabel: "720p",
    detail: "Sharper video with higher data usage.",
  },
];

function getSavedViewingQuality() {
  try {
    const saved = String(localStorage.getItem("ga_stream_quality") || "auto");
    return MUX_VIEWING_OPTIONS.some((option) => option.value === saved)
      ? saved
      : "auto";
  } catch {
    return "auto";
  }
}

export default function Stream() {
  const params = useParams();

  const streamKey =
    params.channelId || params.id || params.slug || params.channel || "";

  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const imaRef = useRef(null);
  const prerollPlayedRef = useRef(false);
  const autoStartInFlightRef = useRef(false);
  const autoStartCompleteRef = useRef(false);
  const midrollInFlightRef = useRef(false);
  const activeLiveWatchMsRef = useRef(0);
  const streamSessionRef = useRef("");
  const hlsResetCountRef = useRef(0);
  const qualityResumeTimeRef = useRef(0);

  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [metaTitle, setMetaTitle] = useState("");
  const [metaGame, setMetaGame] = useState("");
  const [isPlayerMuted, setIsPlayerMuted] = useState(true);
  const [isAdRunning, setIsAdRunning] = useState(false);
  const [adSoundEnabled, setAdSoundEnabled] = useState(false);
  const [imaInitDone, setImaInitDone] = useState(false);
  const [viewingStarted, setViewingStarted] = useState(false);
  const [contentSessionKey, setContentSessionKey] = useState(0);
  const [memberAdFree, setMemberAdFree] = useState(false);
  const [memberCheckDone, setMemberCheckDone] = useState(false);
  const [viewingQuality, setViewingQuality] = useState(getSavedViewingQuality);
  const [viewingStatus, setViewingStatus] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function checkMembership() {
      const email = getCurrentUserEmail();
      if (!email) {
        setMemberAdFree(false);
        setMemberCheckDone(true);
        return;
      }

      try {
        setMemberCheckDone(false);
        const res = await fetch(`${getApiBase()}/api/subscriptions/me?email=${encodeURIComponent(email)}`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        const active = !!(data?.member?.active || data?.member === true);
        setMemberAdFree(active);
        try {
          localStorage.setItem("member_email", email);
          localStorage.setItem("member_status", active ? "active" : "inactive");
        } catch {
          // ignore
        }
      } catch {
        if (!cancelled) setMemberAdFree(false);
      } finally {
        if (!cancelled) setMemberCheckDone(true);
      }
    }

    checkMembership();
    window.addEventListener("auth:changed", checkMembership);
    window.addEventListener("storage", checkMembership);

    return () => {
      cancelled = true;
      window.removeEventListener("auth:changed", checkMembership);
      window.removeEventListener("storage", checkMembership);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let pollId;

    async function load({ silent = false } = {}) {
      try {
        if (!silent) {
          setLoading(true);
          setErr("");
          setPayload(null);
        }

        if (!streamKey) throw new Error("Stream not found");

        const res = await fetch(`/api/streams/${encodeURIComponent(streamKey)}`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error("Stream not found");

        const data = await res.json().catch(() => ({}));
        if (cancelled) return;

        if (!data || data.ok === false) throw new Error("Stream not found");
        setPayload((prev) => {
          const next = data || null;
          const prevLive = Boolean(prev?.player?.live ?? prev?.player?.isLive ?? prev?.isLive ?? false);
          const nextLive = Boolean(next?.player?.live ?? next?.player?.isLive ?? next?.isLive ?? false);
          const prevPid = String(prev?.player?.playbackId || prev?.channel?.playbackId || "");
          const nextPid = String(next?.player?.playbackId || next?.channel?.playbackId || "");

          // Ignore a transient offline poll if it still points at the same active playback.
          if (prev && prevLive && !nextLive && prevPid && nextPid === prevPid) {
            return prev;
          }

          return next;
        });
        setErr("");
      } catch (e) {
        if (cancelled) return;
        if (!silent) setErr(e?.message || "Stream not found");
      } finally {
        if (!cancelled && !silent) setLoading(false);
      }
    }

    const scheduleNext = () => {
      if (cancelled) return;
      const delay = 18_000 + Math.floor(Math.random() * 4_000);
      pollId = window.setTimeout(async () => {
        await load({ silent: true });
        scheduleNext();
      }, delay);
    };

    load().finally(scheduleNext);

    return () => {
      cancelled = true;
      window.clearTimeout(pollId);
    };
  }, [streamKey]);

  const channel = useMemo(() => payload?.channel || payload || null, [payload]);

  const streamMeta = useMemo(() => {
    const m = payload?.channel?.streamMeta || payload?.streamMeta || null;
    return m && typeof m === "object" ? m : { title: "", game: "" };
  }, [payload]);

  useEffect(() => {
    setMetaTitle(String(streamMeta?.title || ""));
    setMetaGame(String(streamMeta?.game || ""));
  }, [streamMeta]);

  useEffect(() => {
    if (!streamKey) return undefined;

    const onMetaUpdate = (data = {}) => {
      const eventSlug = String(data?.slug || "").trim().toLowerCase();
      const activeSlug = String(streamKey || "").trim().toLowerCase();
      if (eventSlug && eventSlug !== activeSlug) return;

      const meta = data?.meta || {};
      const nextTitle = String(meta?.title || "");
      const nextGame = String(meta?.game || "");
      setMetaTitle((prev) => (prev === nextTitle ? prev : nextTitle));
      setMetaGame((prev) => (prev === nextGame ? prev : nextGame));
    };

    socket.on("stream:meta:update", onMetaUpdate);
    return () => socket.off("stream:meta:update", onMetaUpdate);
  }, [streamKey]);

  const playbackId = useMemo(() => {
    return (
      payload?.player?.playbackId ||
      channel?.playbackId ||
      channel?.lastVodPlaybackId ||
      channel?.lastVodPlayback ||
      ""
    );
  }, [payload, channel]);

  const isLive = useMemo(() => {
    return Boolean(
      payload?.player?.live ??
        payload?.player?.isLive ??
        payload?.isLive ??
        channel?.isLive ??
        false
    );
  }, [payload, channel]);

  const room = useMemo(() => {
    if (payload?.chat?.room) return payload.chat.room;
    const roomKey = channel?.slug || streamKey || "unknown";
    return `${isLive ? "live" : "vod"}:${roomKey}`;
  }, [payload, channel, streamKey, isLive]);

  const watchSlug = useMemo(() => {
    return String(channel?.slug || streamKey || "").trim();
  }, [channel, streamKey]);

  useEffect(() => {
    if (!watchSlug) return;

    const token = getToken();
    socket.auth = { token };

    const join = () => {
      socket.emit("watch:join", { channel: watchSlug, token });
      socket.emit("watch:ping", { channel: watchSlug, token });
    };

    if (!socket.connected) {
      socket.connect();
    } else {
      join();
    }

    socket.on("connect", join);

    const pingId = setInterval(() => {
      const t = getToken();
      socket.auth = { token: t };
      if (socket.connected) {
        socket.emit("watch:ping", { channel: watchSlug, token: t });
      }
    }, 20000);

    return () => {
      clearInterval(pingId);
      socket.off("connect", join);
      const t = getToken();
      socket.auth = { token: t };
      if (socket.connected) {
        socket.emit("watch:leave", { channel: watchSlug, token: t });
      }
    };
  }, [watchSlug]);

  const title = useMemo(() => {
    const t = String(metaTitle || "").trim();
    if (t) return t;
    return channel?.name || channel?.title || channel?.id || "Stream";
  }, [channel, metaTitle]);

  const game = useMemo(() => {
    return String(metaGame || "").trim();
  }, [metaGame]);

  const hlsSrc = useMemo(() => {
    if (!playbackId) return "";
    const base = `https://stream.mux.com/${playbackId}.m3u8`;
    return viewingQuality && viewingQuality !== "auto"
      ? `${base}?max_resolution=${encodeURIComponent(viewingQuality)}`
      : base;
  }, [playbackId, viewingQuality]);

  const changeViewingQuality = useCallback((nextValue) => {
    const next = MUX_VIEWING_OPTIONS.some((option) => option.value === nextValue)
      ? nextValue
      : "auto";
    const videoEl = videoRef.current;
    if (videoEl && Number.isFinite(videoEl.currentTime) && videoEl.currentTime > 0) {
      qualityResumeTimeRef.current = videoEl.currentTime;
    }
    try {
      localStorage.setItem("ga_stream_quality", next);
    } catch {
      // ignore storage failures
    }
    setViewingStatus(
      next === "auto"
        ? "Automatic quality is on."
        : `Quality capped at ${next} to reduce data use.`
    );
    setViewingQuality(next);
  }, []);

  const requestPictureInPicture = useCallback(async () => {
    const videoEl = videoRef.current;
    if (!videoEl) {
      setViewingStatus("The video is not ready for pop-out yet.");
      return false;
    }

    try {
      if (
        document.pictureInPictureElement &&
        typeof document.exitPictureInPicture === "function"
      ) {
        await document.exitPictureInPicture();
        setViewingStatus("Picture-in-Picture closed.");
        return true;
      }

      if (
        document.pictureInPictureEnabled &&
        typeof videoEl.requestPictureInPicture === "function"
      ) {
        if (videoEl.paused) {
          try {
            await videoEl.play();
          } catch {
            // A playing video is preferred, but the browser may still allow PiP.
          }
        }
        await videoEl.requestPictureInPicture();
        setViewingStatus("Stream popped out. You can keep watching while using other apps when your device allows it.");
        return true;
      }

      if (
        typeof videoEl.webkitSupportsPresentationMode === "function" &&
        videoEl.webkitSupportsPresentationMode("picture-in-picture") &&
        typeof videoEl.webkitSetPresentationMode === "function"
      ) {
        videoEl.webkitSetPresentationMode("picture-in-picture");
        setViewingStatus("Picture-in-Picture opened.");
        return true;
      }

      setViewingStatus("Picture-in-Picture is not available in this browser/app build yet.");
      return false;
    } catch (error) {
      console.warn("[player] picture-in-picture failed", error?.message || error);
      setViewingStatus("Picture-in-Picture could not start on this device.");
      return false;
    }
  }, []);

  const pictureInPictureAvailable =
    typeof document !== "undefined" &&
    (Boolean(document.pictureInPictureEnabled) ||
      Boolean(
        videoRef.current &&
          typeof videoRef.current.webkitSupportsPresentationMode === "function"
      ));


  const restartContentSession = useCallback(async () => {
    try {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    } catch {
      // ignore
    }

    const videoEl = videoRef.current;
    if (videoEl) {
      try {
        videoEl.pause();
      } catch {
        // ignore
      }
      try {
        videoEl.removeAttribute("src");
        videoEl.load();
      } catch {
        // ignore
      }
    }

    setContentSessionKey((k) => k + 1);
  }, []);
  const PREROLL_KEY = useMemo(
    () => `ga_preroll_stream_${String(streamKey || playbackId || "default")}`,
    [streamKey, playbackId]
  );

  useEffect(() => {
    streamSessionRef.current = PREROLL_KEY;
    prerollPlayedRef.current = false;
    autoStartInFlightRef.current = false;
    autoStartCompleteRef.current = false;
    midrollInFlightRef.current = false;
    activeLiveWatchMsRef.current = 0;
    hlsResetCountRef.current = 0;
    setIsPlayerMuted(true);
    setIsAdRunning(false);
    setAdSoundEnabled(false);
    setImaInitDone(false);
    setViewingStarted(false);
    setContentSessionKey(0);
  }, [PREROLL_KEY, hlsSrc]);

  useEffect(() => {
    let alive = true;
    const videoEl = videoRef.current;

    const restoreVodPosition = () => {
      if (!alive || isLive) return;
      const resumeAt = Number(qualityResumeTimeRef.current || 0);
      if (!resumeAt || !videoEl) return;
      try {
        if (Number.isFinite(videoEl.duration) && videoEl.duration > resumeAt) {
          videoEl.currentTime = resumeAt;
          qualityResumeTimeRef.current = 0;
        }
      } catch {
        // ignore position restore failures
      }
    };

    videoEl?.addEventListener("loadedmetadata", restoreVodPosition);

    (async () => {
      const videoEl = videoRef.current;
      if (!videoEl || !hlsSrc) return;

      try {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      } catch {
        // ignore
      }

      videoEl.playsInline = true;
      videoEl.setAttribute("playsinline", "");
      videoEl.setAttribute("webkit-playsinline", "");
      videoEl.crossOrigin = "anonymous";

      const mod = await import("hls.js");
      const Hls = mod.default;

      if (!alive) return;

      if (Hls.isSupported()) {
        let mediaRecoverAttempts = 0;
        let resetQueued = false;

        const hls = new Hls({
          // Keep client playback conservative. Mux controls the actual live latency profile.
          // Enabling hls.js low-latency mode here has caused short live playback caps/rebuffers.
          lowLatencyMode: false,

          // Long live sessions can expose a huge DVR window. Starting near the live edge and
          // keeping a smaller local buffer prevents Chrome from choking with bufferAppendError.
          startPosition: isLive ? -1 : 0,
          maxBufferLength: isLive ? 12 : 30,
          maxMaxBufferLength: isLive ? 30 : 90,
          backBufferLength: isLive ? 12 : 30,
          liveBackBufferLength: isLive ? 0 : undefined,

          liveDurationInfinity: !!isLive,
          liveSyncDurationCount: isLive ? 3 : undefined,
          liveMaxLatencyDurationCount: isLive ? 10 : undefined,
          enableWorker: true,
        });

        hlsRef.current = hls;

        const jumpToLiveEdge = () => {
          if (!isLive || !alive) return;

          try {
            const syncPosition = hls.liveSyncPosition;
            if (Number.isFinite(syncPosition) && syncPosition > 0) {
              videoEl.currentTime = syncPosition;
              return;
            }
          } catch {
            // ignore
          }

          try {
            if (Number.isFinite(videoEl.duration) && videoEl.duration > 20) {
              videoEl.currentTime = Math.max(0, videoEl.duration - 6);
            }
          } catch {
            // ignore
          }
        };

        const queueCleanReset = (reason) => {
          if (resetQueued || !alive) return;
          resetQueued = true;

          hlsResetCountRef.current += 1;
          console.log("[HLS] clean reset", reason, `attempt ${hlsResetCountRef.current}`);

          try {
            hls.destroy();
          } catch {
            // ignore
          }

          if (hlsRef.current === hls) hlsRef.current = null;

          try {
            videoEl.pause();
          } catch {
            // ignore
          }

          try {
            videoEl.removeAttribute("src");
            videoEl.load();
          } catch {
            // ignore
          }

          if (hlsResetCountRef.current <= 3) {
            setTimeout(() => {
              if (alive) setContentSessionKey((k) => k + 1);
            }, 450);
          } else {
            setErr("Video buffer failed. Please refresh the stream page.");
          }
        };

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          if (!isLive) return;
          setTimeout(jumpToLiveEdge, 250);
          setTimeout(jumpToLiveEdge, 1000);
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data?.fatal) return;

          console.log("[HLS] fatal error", data?.type, data?.details);

          if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
            try {
              hls.startLoad(isLive ? -1 : undefined);
            } catch {
              queueCleanReset("network_error");
            }
            return;
          }

          if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
            const isBufferAppendError = data.details === "bufferAppendError";

            // bufferAppendError can get stuck in a repeat loop after long streams or after
            // switching from ad/live/VOD. A clean media reset is safer than endless recovery.
            if (isBufferAppendError) {
              queueCleanReset("bufferAppendError");
              return;
            }

            if (mediaRecoverAttempts < 2) {
              mediaRecoverAttempts += 1;
              try {
                hls.recoverMediaError();
                if (isLive) setTimeout(jumpToLiveEdge, 250);
                return;
              } catch {
                // fall through to clean reset
              }
            }

            queueCleanReset("media_error");
            return;
          }

          queueCleanReset("fatal_other");
        });

        hls.loadSource(hlsSrc);
        hls.attachMedia(videoEl);
        return;
      }

      const canNative = videoEl.canPlayType("application/vnd.apple.mpegurl");
      if (canNative) {
        try {
          videoEl.pause();
        } catch {
          // ignore
        }
        videoEl.removeAttribute("src");
        videoEl.src = hlsSrc;
        videoEl.load();
        return;
      }

      videoEl.removeAttribute("src");
      videoEl.src = hlsSrc;
      videoEl.load();
    })();

    return () => {
      alive = false;
      videoEl?.removeEventListener("loadedmetadata", restoreVodPosition);
      try {
        if (hlsRef.current) {
          hlsRef.current.destroy();
          hlsRef.current = null;
        }
      } catch {
        // ignore
      }
    };
  }, [hlsSrc, isLive, contentSessionKey]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (!videoEl) return;

    videoEl.defaultMuted = !!isPlayerMuted;
    videoEl.muted = !!isPlayerMuted;
  }, [isPlayerMuted]);

  const playContentWithAutoplayFallback = useCallback(async () => {
    const videoEl = videoRef.current;
    if (!videoEl || !hlsSrc) return false;

    videoEl.volume = 1;
    videoEl.defaultMuted = false;
    videoEl.muted = false;

    try {
      await videoEl.play();
      setIsPlayerMuted(false);
      return true;
    } catch {
      // Browsers that block audible autoplay still allow a muted stream to start.
    }

    videoEl.defaultMuted = true;
    videoEl.muted = true;
    setIsPlayerMuted(true);

    try {
      await videoEl.play();
      return true;
    } catch (e) {
      console.log("[PLAYER] autoplay blocked", e);
      return false;
    }
  }, [hlsSrc]);

  const handleEnableAdSound = useCallback(() => {
    const ctrl = imaRef.current;
    const videoEl = videoRef.current;

    // This runs synchronously inside the viewer's tap/click, which satisfies
    // browser sound policies for both IMA's ad player and the stream video.
    ctrl?.setVolume(1);

    if (videoEl) {
      videoEl.volume = 1;
      videoEl.defaultMuted = false;
      videoEl.muted = false;
    }

    setIsPlayerMuted(false);
    setAdSoundEnabled(true);
  }, []);

  useEffect(() => {
    const videoEl = videoRef.current;
    const adContainerEl = document.getElementById("ima-ad-container");

    setImaInitDone(false);

    try {
      if (imaRef.current) {
        imaRef.current.destroy();
        imaRef.current = null;
      }
    } catch {
      // ignore
    }

    if (!videoEl || !adContainerEl || !hlsSrc || !memberCheckDone) return;
    if (memberAdFree || !DEFAULT_PREROLL_TAG) {
      setImaInitDone(true);
      return;
    }

    const ctrl = new ImaController({
      videoEl,
      adContainerEl,
      adTagUrl: () => withCorrelator(DEFAULT_PREROLL_TAG),
      willAutoPlay: true,
      willPlayMuted: true,
      onLog: (...args) => console.log("[ADS]", ...args),
      onAdBreakStart: () => {
        setIsAdRunning(true);
        setAdSoundEnabled(!ctrl.isMuted());
        try {
          hlsRef.current?.stopLoad?.();
        } catch {
          // ignore
        }
      },
      onAdBreakEnd: () => {
        setIsAdRunning(false);
        restartContentSession();
      },
    });

    imaRef.current = ctrl;

    ctrl
      .init()
      .catch((e) => {
        console.log("[ADS] IMA init failed", e);
      })
      .finally(() => {
        if (imaRef.current === ctrl) setImaInitDone(true);
      });

    return () => {
      try {
        ctrl.destroy();
      } catch {
        // ignore
      }
      if (imaRef.current === ctrl) imaRef.current = null;
    };
  }, [hlsSrc, isLive, contentSessionKey, restartContentSession, memberAdFree, memberCheckDone]);

  useEffect(() => {
    if (!hlsSrc || !memberCheckDone || autoStartCompleteRef.current) return;
    if (autoStartInFlightRef.current) return;
    if (!memberAdFree && DEFAULT_PREROLL_TAG && !imaInitDone) return;

    const sessionKey = PREROLL_KEY;
    const id = setTimeout(async () => {
      if (streamSessionRef.current !== sessionKey) return;
      if (autoStartInFlightRef.current || autoStartCompleteRef.current) return;

      autoStartInFlightRef.current = true;

      try {
        let prerollAlreadySeen = false;
        try {
          prerollAlreadySeen = sessionStorage.getItem(sessionKey) === "1";
        } catch {
          // ignore
        }

        if (!memberAdFree && !prerollAlreadySeen && !prerollPlayedRef.current) {
          const videoEl = videoRef.current;
          const ctrl = imaRef.current;

          prerollPlayedRef.current = true;
          try {
            sessionStorage.setItem(sessionKey, "1");
          } catch {
            // ignore
          }

          if (videoEl && ctrl) {
            const startMuted = !canAutoplayWithSound();

            videoEl.volume = 1;
            videoEl.defaultMuted = startMuted;
            videoEl.muted = startMuted;
            setIsPlayerMuted(startMuted);
            setAdSoundEnabled(!startMuted);

            console.log(
              "[ADS] Starting automatic preroll request",
              startMuted ? "muted" : "with sound"
            );
            await ctrl.requestAndPlayPreroll({
              autoPlay: true,
              muted: startMuted,
            });
          }
        } else {
          prerollPlayedRef.current = true;
        }

        if (streamSessionRef.current !== sessionKey) return;
        autoStartCompleteRef.current = true;
        setViewingStarted(true);
      } catch (e) {
        console.log("[ADS] Automatic preroll failed", e);
        if (streamSessionRef.current === sessionKey) {
          autoStartCompleteRef.current = true;
          setIsAdRunning(false);
          setViewingStarted(true);
        }
      } finally {
        autoStartInFlightRef.current = false;
      }
    }, 0);

    return () => clearTimeout(id);
  }, [hlsSrc, memberCheckDone, memberAdFree, imaInitDone, PREROLL_KEY]);

  useEffect(() => {
    if (!viewingStarted || !hlsSrc || isAdRunning) return;

    const id = setTimeout(() => {
      playContentWithAutoplayFallback();
    }, 180);

    return () => clearTimeout(id);
  }, [contentSessionKey, hlsSrc, isAdRunning, playContentWithAutoplayFallback, viewingStarted]);

  useEffect(() => {
    if (!viewingStarted || !isLive || !memberCheckDone || memberAdFree) return;
    if (!imaInitDone || !DEFAULT_PREROLL_TAG || isAdRunning) return;

    const sessionKey = PREROLL_KEY;
    const id = setInterval(async () => {
      if (streamSessionRef.current !== sessionKey || midrollInFlightRef.current) return;

      const videoEl = videoRef.current;
      if (!videoEl || document.visibilityState !== "visible") return;
      if (videoEl.paused || videoEl.ended || videoEl.readyState < 2) return;

      activeLiveWatchMsRef.current += ACTIVE_WATCH_TICK_MS;
      if (activeLiveWatchMsRef.current < LIVE_MIDROLL_INTERVAL_MS) return;

      activeLiveWatchMsRef.current = 0;
      const ctrl = imaRef.current;
      if (!ctrl) return;

      midrollInFlightRef.current = true;
      try {
        console.log("[ADS] Starting scheduled live midroll request");
        await ctrl.requestAndPlayAd(undefined, {
          autoPlay: true,
          muted: !!videoEl.muted,
        });
      } catch (e) {
        console.log("[ADS] Live midroll failed", e);
      } finally {
        midrollInFlightRef.current = false;
      }
    }, ACTIVE_WATCH_TICK_MS);

    return () => clearInterval(id);
  }, [
    PREROLL_KEY,
    imaInitDone,
    isAdRunning,
    isLive,
    memberAdFree,
    memberCheckDone,
    viewingStarted,
  ]);

  if (loading) {
    return (
      <div className="page">
        <h2>Loading stream…</h2>
      </div>
    );
  }

  if (err) {
    return (
      <div className="page">
        <h2>STREAM NOT FOUND</h2>
        <div style={{ opacity: 0.8, marginBottom: 10 }}>{err}</div>
        <Link to="/streams">← Back to channels</Link>
      </div>
    );
  }

  return (
    <div className="stream-page">
      <div className="stream-left">
        <div className="stream-topbar">
          <div className="stream-topbar__left">
            <Link to="/streams" className="stream-backlink">
              ← Channels
            </Link>

            <div className="stream-topbar__meta">
              <div className="stream-topbar__title">
                {title}
                {isLive ? " • LIVE" : ""}
              </div>
              {game ? (
                <div className="stream-topbar__game">{game}</div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="player-wrap">
          {playbackId ? (
            <div
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "16 / 9",
                background: "#000",
                overflow: "hidden",
                borderRadius: 12,
              }}
            >
              <video
                ref={videoRef}
                controls
                playsInline
                preload="metadata"
                muted={isPlayerMuted}
                crossOrigin="anonymous"
                poster={playbackId ? `https://image.mux.com/${playbackId}/thumbnail.png?time=1` : ""}
                style={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: "100%",
                  display: "block",
                  background: "#000",
                  objectFit: "contain",
                  zIndex: 1,
                  pointerEvents: isAdRunning ? "none" : "auto",
                }}
              />

              {!isAdRunning ? (
                <StreamViewingMenu
                  qualityOptions={MUX_VIEWING_OPTIONS}
                  qualityValue={viewingQuality}
                  onQualityChange={changeViewingQuality}
                  onPictureInPicture={requestPictureInPicture}
                  pictureInPictureAvailable={pictureInPictureAvailable}
                  sourceLabel={isLive ? "GAStreaming live • Mux" : "GAStreaming replay • Mux"}
                  statusText={
                    viewingStatus ||
                    (viewingQuality === "auto"
                      ? "Use Data Saver on slow mobile data. Pop-out keeps the video floating when the device/browser supports Picture-in-Picture."
                      : `Bandwidth cap: ${viewingQuality}. This cap is applied at the Mux manifest, not just the player UI.`)
                  }
                />
              ) : null}

              <div
                id="ima-ad-container"
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 30,
                  pointerEvents: isAdRunning ? "auto" : "none",
                }}
              />

              {isAdRunning && !adSoundEnabled ? (
                <button
                  type="button"
                  onClick={handleEnableAdSound}
                  aria-label="Turn on ad sound"
                  style={{
                    position: "absolute",
                    top: 14,
                    right: 14,
                    zIndex: 45,
                    pointerEvents: "auto",
                    border: "1px solid rgba(255,255,255,0.35)",
                    borderRadius: 999,
                    background: "rgba(0,0,0,0.82)",
                    color: "#fff",
                    padding: "10px 15px",
                    fontSize: 15,
                    fontWeight: 800,
                    lineHeight: 1,
                    cursor: "pointer",
                    boxShadow: "0 4px 14px rgba(0,0,0,0.38)",
                  }}
                >
                  🔊 Tap for sound
                </button>
              ) : null}

              <RpsOverlayLayer room={room} />
              <GasPointsOverlayLayer room={room} />
              <CheerOverlayLayer
                channelId={channel?.slug || channel?.id || streamKey}
                room={room}
                mode="embedded"
                showMeta={true}
                showDebug={false}
                videoScale={0.95}
                videoTop={50}
                videoLeft={50}
                videoWidth={34}
              />

            </div>
          ) : (
            <div className="stream-missing" style={{ padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>
                {channel?.consoleStreamingEnabled ? "Console stream ready" : "No video yet"}
              </h3>
              <div style={{ opacity: 0.8 }}>
                {channel?.consoleStreamingEnabled
                  ? "Waiting for the PS5/Xbox broadcast. Start Broadcast on the console with the Twitch account paired in Console Streaming, then give it a few seconds to appear here."
                  : "This channel doesn’t have a playbackId configured."}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="stream-right">
        <ChatBox room={room} channelId={channel?.id || streamKey} />
      </div>
    </div>
  );
}
